import styled from '@emotion/styled';
import { Fonts, Spacings, mediaQuery } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import BottomChevron from 'aquilla/icons/actions/BottomChevron';
import { useEffect, useState } from 'react';
import useWindowSize from '@hooks/useWindowSize';
import {
  Select as SelectCore,
  SelectInput,
  SelectList,
  SelectOption
} from 'aquilla/core/molecules/Select';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { useRouter } from 'next/router';
import { STORE_TAB_FILTER_CLICKED } from './constants';

const MobileFilterWrapper = styled.div<{ $filterApplied: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_3B} ${Spacings.SPACING_2B}
    ${Spacings.SPACING_4B};
  border: ${({ $filterApplied }) =>
    $filterApplied
      ? `${Spacings.SPACING_1} solid var(--color-base-fill);`
      : `${Spacings.SPACING_1} solid var(--color-i-divider);`};
  border-radius: 68px;
  height: ${Spacings.SPACING_10B};
`;
const FilterText = styled(Typography)<{ $filterApplied: boolean }>`
  font-weight: ${({ $filterApplied }) =>
    $filterApplied ? `${Fonts.SEMIBOLD}` : ''};
`;

const InnerFilterWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${Spacings.SPACING_4B};
  width: max-content;
`;

const StyledSelectInput = styled(SelectInput)<{ $filterApplied: boolean }>`
  padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_3B} ${Spacings.SPACING_2B}
    ${Spacings.SPACING_4B};
  border-color: var(--color-text-primary);
  border: ${({ $filterApplied }) =>
    $filterApplied
      ? `${Spacings.SPACING_1} solid var(--color-base-fill);`
      : `${Spacings.SPACING_1} solid var(--color-i-divider);`};
  min-height: ${Spacings.SPACING_10B};
  gap: ${Spacings.SPACING_3B};
  border-radius: 24px;
  span {
    font-weight: ${({ $filterApplied }) =>
      $filterApplied ? `${Fonts.SEMIBOLD}` : `${Fonts.LIGHT}`};
    font-size: ${Fonts.H6};
  }
  svg {
    color: var(--color-text-primary);
  }
`;

const StyledSelectCore = styled(SelectCore)``;

const StyledSelectionList = styled(SelectList)`
  position: absolute;
  overflow-y: scroll !important;
  margin-top: 8px;
  z-index: 2;
  top: auto !important;
  left: auto !important;
`;

const ALL = 'All';

const StoreFilter = ({
  setOpenBottomSheet,
  filter,
  selected = [],
  onChange = (emptyObject) => {},
  onSelection = (emptyObject) => {}
}) => {
  const { key: filterName, label, selectType: filterType } = filter;
  const { isMobile } = useWindowSize();

  const getSelectedLabelByValue = (value, label = '') => {
    const selectedOption = filter.values.find(
      (filter) => filter.code === value
    );
    return filter.key === 'sort_by'
      ? `${label}: ${selectedOption?.label}`
      : `${selectedOption?.label}`;
  };

  const getSelectedLabel = () => {
    let name = label;

    if (selected?.includes(ALL)) {
      name = label;
    } else if (selected?.length === 1) {
      name = getSelectedLabelByValue(selected[0], label);
    } else if (selected?.length > 1) {
      name = `${selected.length} ${filter.label}`;
    }
    return name;
  };

  const [selectedFilter, setSelectedFilter] = useState(getSelectedLabel);

  useEffect(() => {
    setSelectedFilter(getSelectedLabel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const getFilterLabel = (value) => {
    const colonIndex = value.indexOf(':');
    const selectedOptionLabel = value.substring(colonIndex + 1).trim();
    const selectedOption = filter.values.find(
      (filter) => filter.label === selectedOptionLabel
    );
    return selectedOption;
  };

  const handleOnSelect = (value) => {
    if (value) {
      const selectedOption = getFilterLabel(value);
      setSelectedFilter(value);
      onChange({ value: selectedOption?.code, filterName, filterType });
    }
  };
  const goalUID = useRouter().query.goalUID as string;
  const goalSegmentData = useGoalEventProps({ goalUID });

  const onFilterPillClick = () => {
    setOpenBottomSheet(filterName);
    onSelection(filter);
    segment.track(STORE_TAB_FILTER_CLICKED, {
      ...goalSegmentData,
      filter_type: filter.label
    });
  };

  const isSelected =
    filter.key === 'sort_by'
      ? selected[0] !== filter.defaultSelection.code
      : selected.length !== 0;

  return (
    <>
      {isMobile ? (
        <MobileFilterWrapper
          $filterApplied={isSelected}
          onClick={onFilterPillClick}
        >
          <InnerFilterWrapper>
            <FilterText $filterApplied={isSelected} variant="p2">
              {selectedFilter}
            </FilterText>
            <BottomChevron height={24} width={24} />
          </InnerFilterWrapper>
        </MobileFilterWrapper>
      ) : (
        <StyledSelectCore
          value={selectedFilter}
          onSelect={handleOnSelect}
          disableDeselect
          className="aquilla-selected-core"
        >
          <StyledSelectInput $filterApplied={isSelected} />
          <StyledSelectionList>
            {filter?.values?.map((option) => {
              return (
                <SelectOption
                  key={option.code}
                  value={`${label}: ${option.label}`}
                  label={option.label}
                />
              );
            })}
          </StyledSelectionList>
        </StyledSelectCore>
      )}
    </>
  );
};
export default StoreFilter;
